import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Image as ImageIcon, Video, Download, Loader2 } from "lucide-react";

export interface AttachmentMeta {
  name: string;
  path: string;
  mime: string;
  size: number;
  url?: string | null;
}

interface Props {
  attachments: AttachmentMeta[];
  bucket: "contact-attachments" | "support-attachments";
}

const iconFor = (mime: string) => {
  if (mime.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
  if (mime.startsWith("video/")) return <Video className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
};

const AttachmentList = ({ attachments, bucket }: Props) => {
  const [loadingPath, setLoadingPath] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Record<string, string>>({});

  if (!attachments || attachments.length === 0) return null;

  const getUrl = async (path: string): Promise<string | null> => {
    if (resolved[path]) return resolved[path];
    setLoadingPath(path);
    try {
      const { data, error } = await supabase.functions.invoke("support-sign-download", {
        body: { bucket, path },
      });
      if (error || !data?.url) return null;
      setResolved((r) => ({ ...r, [path]: data.url }));
      return data.url;
    } finally {
      setLoadingPath(null);
    }
  };

  const open = async (a: AttachmentMeta) => {
    const url = a.url || (await getUrl(a.path));
    if (url) window.open(url, "_blank", "noopener");
  };

  return (
    <ul className="space-y-1.5 mt-2">
      {attachments.map((a) => (
        <li
          key={a.path}
          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-primary/5 border border-primary/15 text-sm"
        >
          <span className="text-primary shrink-0">{iconFor(a.mime)}</span>
          <span className="truncate flex-1 min-w-0">{a.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {(a.size / 1024 / 1024).toFixed(2)} Mo
          </span>
          <button
            type="button"
            onClick={() => open(a)}
            disabled={loadingPath === a.path}
            className="p-1 rounded text-primary hover:bg-primary/10"
            aria-label={`Ouvrir ${a.name}`}
          >
            {loadingPath === a.path ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
          </button>
        </li>
      ))}
    </ul>
  );
};

export default AttachmentList;
